'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

export default function DigitalMarketingHeroSection() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section className="relative w-full min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-orange-600 via-pink-600 to-purple-700">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      {/* Floating Marketing Icons */}
      <div className="absolute inset-0 pointer-events-none">
        <Icon name="MegaphoneIcon" size={60} className="absolute top-20 right-10 text-white/20 animate-pulse delay-100" />
        <Icon name="ChartBarIcon" size={50} className="absolute top-40 left-20 text-white/20 animate-pulse delay-300" />
        <Icon name="GlobeAltIcon" size={70} className="absolute bottom-32 right-20 text-white/20 animate-pulse delay-500" />
        <Icon name="LightBulbIcon" size={55} className="absolute bottom-20 left-32 text-white/20 animate-pulse delay-700" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Image */}
          <div className={`relative order-2 md:order-1 ${isVisible ? 'animate-fadeInScale delay-400' : 'opacity-0'}`}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white/30">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_100904d7d-1767488323598.png"
                alt="Digital marketing workspace with laptop showing analytics dashboard, smartphone, and marketing strategy documents with colorful charts and graphs"
                width={800}
                height={600}
                className="w-full h-auto" />
              
              <div className="absolute inset-0 bg-gradient-to-t from-orange-900/80 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <p className="text-white font-display text-2xl uppercase tracking-tight">
                  "Market smart.<br />Grow faster."
                </p>
              </div>
            </div>
          </div>

          {/* Right Content */}
          <div className={`order-1 md:order-2 ${isVisible ? 'animate-fadeInUp delay-200' : 'opacity-0'}`}>
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md border border-white/30 rounded-full px-4 py-2 mb-6">
              <Icon name="SparklesIcon" size={16} className="text-white" />
              <span className="text-xs font-semibold uppercase tracking-wider text-white">
                100% Job Oriented
              </span>
            </div>

            <h2 className="font-display text-5xl md:text-7xl uppercase tracking-tighter leading-none text-white mb-6">
              Turn Clicks
              <br />
              Into <span className="text-yellow-300">Careers</span>
            </h2>

            <p className="text-lg text-white/90 mb-6 leading-relaxed">
              Forget boring theory. Learn <span className="text-yellow-300 font-semibold">creative digital marketing</span> that drives real results, builds brands, and opens career doors.
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <Icon name="CheckCircleIcon" size={24} className="text-yellow-300 flex-shrink-0 mt-1" />
                <p className="text-white/90">
                  <span className="font-semibold text-white">Master modern marketing:</span> SEO, Social Media, Ads, Content & Analytics
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Icon name="CheckCircleIcon" size={24} className="text-yellow-300 flex-shrink-0 mt-1" />
                <p className="text-white/90">
                  <span className="font-semibold text-white">Campaign-driven learning:</span> Create real campaigns, not just presentations
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Icon name="CheckCircleIcon" size={24} className="text-yellow-300 flex-shrink-0 mt-1" />
                <p className="text-white/90">
                  <span className="font-semibold text-white">Industry-certified skills:</span> 100% job-oriented training with live projects
                </p>
              </div>
            </div>

            <Link
              href="/courses"
              className="group inline-flex items-center gap-2 bg-white text-orange-600 px-8 py-4 rounded-full text-base font-semibold uppercase tracking-wider hover:bg-white/90 transition-all shadow-xl hover:shadow-2xl hover:scale-105">
              
              Launch Marketing Career
              <Icon name="ArrowRightIcon" size={20} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </section>);

}